import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  safeGetPersistedValue,
  safePersistValue,
} from '@lifeomic/phc-web-toolkit/src/hooks/useLocalStorageState';

import {
  Conversations,
  RecentConversationTitlePosts,
} from '../graphql/queries';
import {
  ConversationsConnection,
  PostOrder,
} from '../__generated__/api.graphql';
import { POPULATION_AGENT_USERNAME } from '../constants';

export const ZORI_RECENT_CONVERSATIONS_KEY = 'zoriRecentConversationsV4';
export const ZORI_HIDDEN_CONVERSATIONS_KEY = 'zoriHiddenConversationsV1';
const CONVERSATION_DISPLAY_TITLE_METADATA_KEY = 'conversationDisplayTitle';

export type RecentConversationStorageEntry = {
  conversationId: string;
  patientId?: string;
  title?: string;
  lastOpenedAt?: string;
  userIds?: string[];
};

export type RecentConversation = RecentConversationStorageEntry & {
  key: string;
  latestMessageText?: string;
  latestMessageTime?: string;
  latestMessageUserId?: string;
  hasUnread?: boolean;
};

type ConversationsQueryResponse = {
  conversations: ConversationsConnection;
};

type RecentConversationTitlePostsQueryResponse = {
  privatePosts: {
    edges: {
      node: {
        __typename?: string;
        metadata?: string | null;
      };
    }[];
  };
};

const normalizeHiddenValue = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(
    entry => typeof entry === 'string' && entry.trim().length > 0,
  );
};

export const getRecentConversationKey = (
  entry: RecentConversationStorageEntry,
) => {
  if (entry.conversationId) return entry.conversationId;
  if (entry.userIds?.length) return [...entry.userIds].sort().join('|');
  if (entry.patientId) return entry.patientId;
  return '';
};

export const getConversationDisplayTitleFromMetadata = (
  metadata?: string | Record<string, unknown> | null,
) => {
  let parsedMetadata: Record<string, unknown> | null | undefined;
  try {
    parsedMetadata =
      typeof metadata === 'string' ? JSON.parse(metadata || '{}') : metadata;
  } catch {
    parsedMetadata = undefined;
  }
  const title = parsedMetadata?.[CONVERSATION_DISPLAY_TITLE_METADATA_KEY];
  return typeof title === 'string' ? title.trim() || undefined : undefined;
};

export const persistRecentConversation = (
  entry: RecentConversationStorageEntry,
  maxEntries = 10,
) => {
  const key = getRecentConversationKey(entry);
  if (!key) return;

  const existing =
    safeGetPersistedValue<RecentConversationStorageEntry[]>(
      ZORI_RECENT_CONVERSATIONS_KEY,
    ) || [];

  const filtered = existing.filter(
    existingEntry => getRecentConversationKey(existingEntry) !== key,
  );

  safePersistValue(
    ZORI_RECENT_CONVERSATIONS_KEY,
    [entry, ...filtered].slice(0, maxEntries),
  );
};

export const removeRecentConversation = (
  entry: RecentConversationStorageEntry,
) => {
  const key = getRecentConversationKey(entry);
  if (!key) return;
  const existing =
    safeGetPersistedValue<RecentConversationStorageEntry[]>(
      ZORI_RECENT_CONVERSATIONS_KEY,
    ) || [];
  const filtered = existing.filter(
    existingEntry => getRecentConversationKey(existingEntry) !== key,
  );
  safePersistValue(ZORI_RECENT_CONVERSATIONS_KEY, filtered);
};

export const useRecentConversations = () => {
  const client = useApolloClient();
  const { data, loading } = useQuery<ConversationsQueryResponse>(
    Conversations,
    {
      variables: { first: 10 },
    },
  );
  const [hiddenConversationIds, setHiddenConversationIds] = useState(() => {
    const stored =
      safeGetPersistedValue<string[]>(ZORI_HIDDEN_CONVERSATIONS_KEY) || [];
    return normalizeHiddenValue(stored);
  });
  const [metadataTitleByConversationId, setMetadataTitleByConversationId] =
    useState<Record<string, string>>({});

  useEffect(() => {
    const conversations = data?.conversations?.edges
      ?.map(edge => edge.node)
      .filter(Boolean);
    const candidates =
      conversations?.filter(
        conversation =>
          !hiddenConversationIds.includes(conversation.conversationId) &&
          !conversation.displayTitle?.trim() &&
          !metadataTitleByConversationId[conversation.conversationId] &&
          conversation.userIds?.includes(POPULATION_AGENT_USERNAME),
      ) ?? [];
    if (!candidates.length) return;

    let isCancelled = false;

    const preloadConversationTitles = async () => {
      const entries = await Promise.all(
        candidates.map(async conversation => {
          try {
            const response =
              await client.query<RecentConversationTitlePostsQueryResponse>({
                query: RecentConversationTitlePosts,
                variables: {
                  userIds: conversation.userIds,
                  first: 10,
                  filter: {
                    order: PostOrder.NEWEST,
                  },
                },
              });
            const title = response.data.privatePosts.edges
              .map(edge =>
                getConversationDisplayTitleFromMetadata(edge.node.metadata),
              )
              .find((candidate): candidate is string => !!candidate);
            return title ? [conversation.conversationId, title] : null;
          } catch {
            return null;
          }
        }),
      );
      if (isCancelled) return;
      setMetadataTitleByConversationId(prev => {
        let didChange = false;
        const next = { ...prev };
        entries.forEach(entry => {
          if (!entry) return;
          const [conversationId, title] = entry;
          if (next[conversationId] === title) return;
          next[conversationId] = title;
          didChange = true;
        });
        return didChange ? next : prev;
      });
    };

    preloadConversationTitles();

    return () => {
      isCancelled = true;
    };
  }, [client, data, hiddenConversationIds, metadataTitleByConversationId]);

  const recentConversations = useMemo<RecentConversation[]>(() => {
    if (!data?.conversations?.edges?.length) return [];
    return data.conversations.edges
      .map(edge => edge.node)
      .filter(Boolean)
      .filter(
        conversation =>
          !hiddenConversationIds.includes(conversation.conversationId),
      )
      .map(conversation => {
        const displayTitle = conversation.displayTitle?.trim();
        const metadataTitle =
          metadataTitleByConversationId[conversation.conversationId];
        const patientIdFromScope = conversation.userIds
          ?.find(id => id.startsWith('scope?subject='))
          ?.split('scope?subject=')[1]
          ?.trim();

        return {
          conversationId: conversation.conversationId,
          userIds: conversation.userIds,
          title: displayTitle || metadataTitle || undefined,
          latestMessageText: conversation.latestMessageText,
          latestMessageTime: conversation.latestMessageTime,
          latestMessageUserId: conversation.latestMessageUserId,
          hasUnread: conversation.hasUnread,
          patientId: patientIdFromScope,
          key: conversation.conversationId,
        };
      });
  }, [data, hiddenConversationIds, metadataTitleByConversationId]);

  const hideConversation = (conversationId?: string) => {
    if (!conversationId) return;
    setHiddenConversationIds(prev => {
      if (prev.includes(conversationId)) return prev;
      const next = [...prev, conversationId];
      safePersistValue(ZORI_HIDDEN_CONVERSATIONS_KEY, next);
      return next;
    });
  };

  const unhideConversation = (conversationId?: string) => {
    if (!conversationId) return;
    setHiddenConversationIds(prev => {
      if (!prev.includes(conversationId)) return prev;
      const next = prev.filter(id => id !== conversationId);
      safePersistValue(ZORI_HIDDEN_CONVERSATIONS_KEY, next);
      return next;
    });
  };

  return {
    recentConversations,
    loading,
    hideConversation,
    unhideConversation,
  };
};
